import type { WebsiteDataFeedSelect } from 'types';
import type { FeedRefreshSeconds } from 'definitions';
import type { FeedDraft, FeedTestResult } from '@/state/useWebsiteFeeds';
import { useRef, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { feedErrorMessage, useWebsiteFeeds } from '@/state/useWebsiteFeeds';
import { describePick, FeedPickFields } from './FeedPickFields';

type DataFeedsSectionProps = { websiteId: number };

const SELECT_TRIGGER = 'border-line bg-field text-foreground h-9 rounded-lg text-body';

const REFRESH_OPTIONS: { value: FeedRefreshSeconds; label: string }[] = [
  { value: 300, label: 'Every 5 minutes' },
  { value: 900, label: 'Every 15 minutes' },
  { value: 3600, label: 'Every hour' },
  { value: 21600, label: 'Every 6 hours' },
  { value: 86400, label: 'Every day' },
  { value: 604800, label: 'Every week' },
];

const EMPTY_DRAFT: FeedDraft = {
  name: '',
  url: '',
  refreshSeconds: 3600,
  pick: 'first',
  matchSource: 'page.path',
  matchField: '',
  matchMode: 'equals',
  startField: '',
  endField: '',
  fields: [],
};

const refreshLabel = (seconds: number) =>
  REFRESH_OPTIONS.find((option) => option.value === seconds)?.label.toLowerCase() ?? `every ${seconds}s`;

const hostOf = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

const toDraft = (feed: WebsiteDataFeedSelect): FeedDraft => ({
  name: feed.name,
  url: feed.url,
  refreshSeconds: feed.refreshSeconds,
  pick: feed.pick,
  matchSource: feed.matchSource ?? 'page.path',
  matchField: feed.matchField ?? '',
  matchMode: feed.matchMode,
  startField: feed.startField ?? '',
  endField: feed.endField ?? '',
  fields: feed.fields,
});

type FeedFormProps = {
  initial: FeedDraft;
  manager: ReturnType<typeof useWebsiteFeeds>;
  feedId?: number;
  onDone: () => void;
};

const FeedForm = ({ initial, manager, feedId, onDone }: FeedFormProps) => {
  const [draft, setDraft] = useState(initial);
  const [test, setTest] = useState<FeedTestResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<'test' | 'save' | null>(null);
  const set = (patch: Partial<FeedDraft>) => setDraft((current) => ({ ...current, ...patch }));
  // The URL in the field now, for a test answer arriving after it was changed.
  const urlRef = useRef(initial.url);

  const runTest = async () => {
    setBusy('test');
    setError('');
    const testedUrl = draft.url;
    try {
      const result = await manager.testFeed(testedUrl);
      // The answer can take ten seconds: it is dropped if the URL changed meanwhile, and merged
      // into the draft as it is now, so nothing picked while waiting is overwritten.
      if (urlRef.current !== testedUrl) return;
      setTest(result);
      setDraft((current) => ({
        ...current,
        matchField: current.matchField || (current.pick === 'match' ? (result.fields[0] ?? '') : ''),
        // First test: every field offered, ticked, for the person to narrow down.
        fields: current.fields.length ? current.fields : result.fields,
      }));
    } catch (ex) {
      setTest(null);
      setError(feedErrorMessage(ex));
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    setBusy('save');
    setError('');
    try {
      await manager.saveFeed(draft, feedId);
      onDone();
    } catch (ex) {
      setError(feedErrorMessage(ex));
    } finally {
      setBusy(null);
    }
  };

  const sampleField = draft.matchField || test?.fields[0];
  const offered = [...new Set([...(test?.fields ?? []), ...draft.fields])];
  const toggleField = (field: string, on: boolean) =>
    setDraft((current) => ({
      ...current,
      fields: on ? [...current.fields, field] : current.fields.filter((item) => item !== field),
    }));
  const renamed = !!feedId && draft.name !== initial.name;

  return (
    <div className="border-line bg-panel2 rounded-card space-y-3 border p-3">
      <div className="grid grid-cols-[140px_1fr] gap-2">
        <Input
          aria-label="Feed name"
          placeholder="reviews"
          value={draft.name}
          onChange={(ev) => set({ name: ev.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
          className="h-9 font-mono"
        />
        <div className="flex gap-2">
          <Input
            aria-label="Feed URL"
            placeholder="https://shop.example/feed.json"
            value={draft.url}
            onChange={(ev) => {
              urlRef.current = ev.target.value;
              set({ url: ev.target.value });
              setTest(null);
            }}
            className="h-9"
          />
          <Button variant="outline" className="h-9 rounded-lg" onClick={runTest} disabled={!draft.url || !!busy}>
            {busy === 'test' ? 'Testing' : 'Test'}
          </Button>
        </div>
      </div>

      {test && (
        <p className="supporting m-0">
          {test.count} {test.count === 1 ? 'item' : 'items'}. Fields: {test.fields.slice(0, 12).join(', ')}
          {test.fields.length > 12 ? ` and ${test.fields.length - 12} more` : ''}
          {sampleField && test.sample[0]?.[sampleField] !== undefined && (
            <>
              . First {sampleField}: <span className="num">{test.sample[0][sampleField]}</span>
            </>
          )}
        </p>
      )}

      <Select
        value={String(draft.refreshSeconds)}
        onValueChange={(value) => set({ refreshSeconds: Number(value) as FeedRefreshSeconds })}
      >
        <SelectTrigger aria-label="Refresh" className={SELECT_TRIGGER}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {REFRESH_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={String(option.value)}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <FeedPickFields draft={draft} fields={offered} set={set} />

      {offered.length > 0 && (
        <fieldset className="m-0 space-y-1.5 border-0 p-0">
          <legend className="micro-label mb-1">Fields pages may show</legend>
          <p className="supporting m-0">
            Visitors receive only these. Leave out anything private, such as cost or supplier fields.
          </p>
          <div className="grid max-h-40 grid-cols-2 gap-x-3 gap-y-1 overflow-y-auto">
            {offered.map((field) => (
              <label key={field} className="text-meta flex min-w-0 cursor-pointer items-center gap-2">
                <Checkbox
                  checked={draft.fields.includes(field)}
                  onCheckedChange={(checked) => toggleField(field, checked === true)}
                />
                <span className="truncate font-mono">{field}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {renamed && (
        <p className="text-meta m-0 text-warn">
          Tokens use the name: components that read {`{{feed.${initial.name}…}}`} will show their fallback until they
          are changed to the new name.
        </p>
      )}

      {error && <p className="text-destructive text-meta m-0">{error}</p>}

      <div className="flex items-center justify-between gap-2">
        <span className="supporting font-mono">
          {draft.name ? `{{feed.${draft.name}.${sampleField ?? 'field'}}}` : ''}
        </span>
        <div className="flex gap-2">
          <Button variant="ghost" className="h-9 rounded-lg" onClick={onDone}>
            Cancel
          </Button>
          <Button
            className="h-9 rounded-lg"
            onClick={save}
            disabled={!draft.name || !draft.url || draft.fields.length === 0 || !!busy}
          >
            {busy === 'save' ? 'Saving' : 'Save feed'}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Feeds this website's components can read in the editor (P-PER-41). Each feed saves on
 * its own, apart from the dialog's Save, because each is its own row.
 */
export const DataFeedsSection = ({ websiteId }: DataFeedsSectionProps) => {
  const manager = useWebsiteFeeds(websiteId);
  const [editing, setEditing] = useState<number | 'new' | null>(null);

  const [wantsToDelete, setWantsToDelete] = useState<WebsiteDataFeedSelect | null>(null);

  const confirmDelete = async () => {
    if (!wantsToDelete) return;
    await manager.deleteFeed(wantsToDelete.id);
    setWantsToDelete(null);
  };

  return (
    <div className="space-y-2">
      <div>
        <span className="micro-label block">Data feeds</span>
        <p className="supporting m-0 mt-1">
          A product, review or stock feed (JSON, or a Google Merchant XML feed). Pertento fetches it on the refresh you
          pick, and components show its values with tokens. Visitors only receive the item that matches their page.
        </p>
      </div>

      {manager.feeds.map((feed) =>
        editing === feed.id ? (
          <FeedForm
            key={feed.id}
            initial={toDraft(feed)}
            manager={manager}
            feedId={feed.id}
            onDone={() => setEditing(null)}
          />
        ) : (
          <div key={feed.id} className="border-line rounded-card flex items-center justify-between gap-3 border p-3">
            <div className="min-w-0">
              <div className="text-foreground text-meta font-mono font-medium">{feed.name}</div>
              <div className="supporting truncate">
                {hostOf(feed.url)}, {refreshLabel(feed.refreshSeconds)}
                {`, ${describePick({ pick: feed.pick, matchSource: feed.matchSource ?? '', matchField: feed.matchField ?? '' })}`}
              </div>
            </div>
            <div className="flex shrink-0 gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Edit ${feed.name}`}
                className="h-8 w-8"
                onClick={() => setEditing(feed.id)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Delete ${feed.name}`}
                className="h-8 w-8"
                onClick={() => setWantsToDelete(feed)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ),
      )}

      {editing === 'new' ? (
        <FeedForm initial={EMPTY_DRAFT} manager={manager} onDone={() => setEditing(null)} />
      ) : (
        <Button variant="outline" className="h-9 rounded-lg" onClick={() => setEditing('new')}>
          <Plus className="mr-1 h-4 w-4" /> Add a feed
        </Button>
      )}

      <ConfirmDialog
        title={`Delete the feed "${wantsToDelete?.name}"?`}
        open={!!wantsToDelete}
        confirmLabel="Delete"
        confirmVariant="destructive"
        onConfirm={confirmDelete}
        onCancel={() => setWantsToDelete(null)}
      >
        Components that read it will show their fallback text instead.
      </ConfirmDialog>
    </div>
  );
};
